import React, { useState, useEffect, useCallback } from "react";

import { fetchRecordCounts } from "../../actions/adminActions";
import { humanFileSize, numberWithCommas } from "../../utilities/utilities";
import DevTransposedTable from "./DevTransposedTable";
import { ColumnPane, SectionControl } from "./ColumnPane";
import config from "../../config/legalworks.frontend.config";

const PRIORITY_TABLES = [
	"Matters",
	"Clients",
	"Folders",
	"Documents",
	"DocumentVersions",
	"Emails",
	"Notes",
	"Users",
];

const DATABASE_TABLE_MIN_WIDTH = 260;

function splitByPriority(tableCounts) {
	const priorityTableCounts = {};
	PRIORITY_TABLES.forEach((tableName) => {
		if (tableName in tableCounts) {
			priorityTableCounts[tableName] = tableCounts[tableName];
		}
	});

	const otherTableCounts = {};
	Object.keys(tableCounts).forEach((tableName) => {
		if (!PRIORITY_TABLES.includes(tableName)) {
			otherTableCounts[tableName] = tableCounts[tableName];
		}
	});

	return { priorityTableCounts, otherTableCounts };
}

function sumValues(obj) {
	if (!obj) return 0;
	return Object.values(obj).reduce((total, value) => total + value, 0);
}

function formatCompactCount(count) {
	if (count == null) return "0";
	return count >= 1000 ? `${Math.round(count / 1000)}k` : String(count);
}

function formatS3Summary(s3) {
	if (!s3?.totalObjects) return null;
	const objectsLabel = `${formatCompactCount(s3.totalObjects)} objects`;
	return s3.totalSizeHuman
		? `${objectsLabel}, ${s3.totalSizeHuman}`
		: objectsLabel;
}

function s3CategoryTableData(s3) {
	return {
		"Docs Objects": numberWithCommas(s3?.docs?.total?.count ?? 0),
		"Docs Size": humanFileSize(s3?.docs?.total?.bytes ?? 0, true),
		"Email Objects": numberWithCommas(s3?.email?.total?.count ?? 0),
		"Email Size": humanFileSize(s3?.email?.total?.bytes ?? 0, true),
	};
}

function s3CategoryTableDataOrphans(s3) {
	return {
		"Docs Objects": numberWithCommas(s3?.orphaned?.docs?.count ?? 0),
		"Docs Size": humanFileSize(s3?.orphaned?.docs?.bytes ?? 0, true),
		"Email Objects": numberWithCommas(s3?.orphaned?.email?.count ?? 0),
		"Email Size": humanFileSize(s3?.orphaned?.email?.bytes ?? 0, true),
	};
}

function AdminRecordCounts({ tenantID, refreshTick }) {
	const [recordCounts, setRecordCounts] = useState(null);
	const [error, setError] = useState(null);

	const handleRefresh = useCallback(() => {
		setError(null);
		fetchRecordCounts(tenantID)
			.then((result) => {
				setRecordCounts(result);
			})
			.catch((err) => {
				setError(
					err?.lxwError?.userMessage ??
						err?.message ??
						"Failed to load record counts"
				);
			});
	}, [tenantID]);

	useEffect(() => {
		handleRefresh();
	}, [handleRefresh, refreshTick]);

	const { priorityTableCounts, otherTableCounts } = recordCounts?.database
		? splitByPriority(recordCounts.database)
		: { priorityTableCounts: null, otherTableCounts: null };

	return (
		<div style={{ padding: 12 }}>
			{error ? (
				<div style={{ color: "var(--lw-darker-red)" }}>{error}</div>
			) : !recordCounts ? (
				<div>Loading record counts...</div>
			) : (
				<div
					style={{
						display: "flex",
						border: "1px solid #e0e0e0",
						borderRadius: 4,
					}}
				>
					<ColumnPane
						title="Database"
						sectionControl={
							<SectionControl
								label="Database"
								count={sumValues(recordCounts.database)}
							/>
						}
					>
						<div style={{ marginBottom: 16 }}>
							<h5 style={{ margin: "0 0 8px 0" }}>Main Tables</h5>
							<DevTransposedTable
								data={priorityTableCounts}
								tableUniqueID="/admin/tenant/records/database/priority"
								fieldLabel="Table"
								valueLabel="Record Count"
								minWidth={DATABASE_TABLE_MIN_WIDTH}
							/>
						</div>
						<div>
							<h5 style={{ margin: "0 0 8px 0" }}>
								All Other Tables
							</h5>
							<DevTransposedTable
								data={otherTableCounts}
								tableUniqueID="/admin/tenant/records/database/other"
								fieldLabel="Table"
								valueLabel="Record Count"
								minWidth={DATABASE_TABLE_MIN_WIDTH}
							/>
						</div>
					</ColumnPane>

					<ColumnPane
						title="S3"
						sectionControl={
							<SectionControl
								label="S3"
								count={recordCounts.s3V2?.total?.count}
								displayValue={formatS3Summary(
									recordCounts.s3V2
								)}
							/>
						}
					>
						<div
							style={{
								color: "#666",
								fontSize: 11,
								fontStyle: "italic",
								marginBottom: 8,
							}}
						>
							{config.variantName === "local"
								? "Must be calculated by you on demand"
								: "Calculated nightly"}
						</div>
						{recordCounts.s3V2 ? (
							<>
								<h5 style={{ margin: "0 0 8px 0" }}>
									Valid Objects
								</h5>
								<DevTransposedTable
									data={s3CategoryTableData(
										recordCounts.s3V2
									)}
									tableUniqueID="/admin/tenant/records/s3"
									fieldLabel="Category"
									valueLabel="Value"
								/>

								<h5 style={{ margin: "16px 0 8px 0" }}>
									Orphaned Objects
								</h5>
								<DevTransposedTable
									data={s3CategoryTableDataOrphans(
										recordCounts.s3V2
									)}
									tableUniqueID="/admin/tenant/records/s3"
									fieldLabel="Category"
									valueLabel="Value"
								/>
							</>
						) : (
							<span
								style={{
									color: "#aaa",
									fontSize: 12,
									fontStyle: "italic",
								}}
							>
								No S3 usage data available
							</span>
						)}
					</ColumnPane>

					<ColumnPane
						title="Search"
						sectionControl={
							<SectionControl
								label="Search"
								count={
									recordCounts.search?.status === "ok"
										? sumValues(recordCounts.search.counts)
										: null
								}
								emptyLabel={
									recordCounts.search?.status === "no_index"
										? "no index"
										: recordCounts.search?.status ===
											  "connection_error"
											? "connection error"
											: undefined
								}
								emptyColor={
									recordCounts.search?.status ===
										"no_index" ||
									recordCounts.search?.status ===
										"connection_error"
										? "var(--lw-darker-red)"
										: undefined
								}
							/>
						}
						isLast
					>
						<DevTransposedTable
							data={recordCounts.search?.counts}
							tableUniqueID="/admin/tenant/records/search"
							fieldLabel="Record Type"
							valueLabel="Count"
						/>
					</ColumnPane>
				</div>
			)}
		</div>
	);
}

export default AdminRecordCounts;
